import * as XLSX from "xlsx";
import { LIMITS, questionIssue } from "../app/muzakere/questionRules";
import { TAGS_MAX, TAG_MAX, parseTags } from "../app/library/libraryRules";

/**
 * Müzakere Kütüphanesi: sorulu paketleri Excel'den toplu yükleme.
 *
 * Format (ilk sayfa, ilk satır başlık; her satır bir soru):
 *   Paket | Açıklama | Etiketler | Soru Tipi | Soru | Şık A … Şık F | Doğru Cevap
 * - Aynı "Paket" adını taşıyan satırlar tek pakette, sırasıyla toplanır;
 *   böylece bir dosyada birden çok paket yüklenebilir.
 * - Açıklama ve Etiketler paketin herhangi bir satırında bir kez yazılır
 *   (farklı iki değer hatadır). Etiketler virgülle ayrılır.
 * - Soru Tipi: Çoktan seçmeli · Evet / Hayır · Doğru / Yanlış · Açık uçlu.
 * - Doğru Cevap: çoktan seçmelide şık harfi (A–F) ya da sırası (1–6);
 *   Evet / Hayır ya da Doğru / Yanlış; açık uçluda boş.
 * Sınırlar paket düzenleyicisi ve sunucuyla aynı (questionRules, libraryRules).
 * Tek bir satır bile hatalıysa hiçbir paket kabul edilmez (ya hep ya hiç).
 */

export const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F"];
export const LIBRARY_HEADERS = [
  "Paket",
  "Açıklama",
  "Etiketler",
  "Soru Tipi",
  "Soru",
  ...OPTION_LETTERS.map((l) => `Şık ${l}`),
  "Doğru Cevap",
];
export const LIBRARY_MAX_PACKAGES = 20;
export const LIBRARY_TEMPLATE_NAME = "muzakere-kutuphanesi-ornek.xlsx";

const TYPE_NAMES = {
  coktan: "Çoktan seçmeli",
  evethayir: "Evet / Hayır",
  dogruyanlis: "Doğru / Yanlış",
  acik: "Açık uçlu",
};

/** Karşılaştırma için: küçük harf (Türkçe), boşluk ve ayraçlar atılır. */
const norm = (value) =>
  String(value ?? "")
    .toLocaleLowerCase("tr")
    .replace(/[\s/\-_.]+/g, "");

const TYPE_BY_NAME = {
  coktanseçmeli: "coktan",
  çoktanseçmeli: "coktan",
  coktan: "coktan",
  çoktan: "coktan",
  evethayır: "evethayir",
  evethayir: "evethayir",
  doğruyanlış: "dogruyanlis",
  dogruyanlis: "dogruyanlis",
  açıkuçlu: "acik",
  acikuclu: "acik",
  açık: "acik",
  acik: "acik",
};

export function parseQuestionType(value) {
  return TYPE_BY_NAME[norm(value)] || null;
}

/** Doğru cevap hücresi → { correctIndex } | { correctBool } | {} ; geçersizse null. */
export function parseCorrect(type, value, optionCount) {
  const raw = String(value ?? "").trim();
  if (type === "acik") return raw ? null : {};
  if (!raw) return null;
  if (type === "coktan") {
    const letter = OPTION_LETTERS.indexOf(raw.toLocaleUpperCase("tr"));
    const index = letter >= 0 ? letter : /^\d+$/.test(raw) ? Number(raw) - 1 : -1;
    return index >= 0 && index < optionCount ? { correctIndex: index } : null;
  }
  const key = norm(raw);
  if (type === "evethayir") {
    if (key === "evet") return { correctBool: true };
    if (key === "hayır" || key === "hayir") return { correctBool: false };
    return null;
  }
  if (key === "doğru" || key === "dogru") return { correctBool: true };
  if (key === "yanlış" || key === "yanlis") return { correctBool: false };
  return null;
}

const QUESTION_ISSUE_TEXT = {
  text: "Soru boş olamaz",
  textLength: `Soru ${LIMITS.questionTextMax} karakterden uzun olamaz`,
  optionsCount: `Çoktan seçmelide ${LIMITS.optionsMin}–${LIMITS.optionsMax} şık olmalı`,
  optionEmpty: "Şıklar arasında boş bırakılan var; şıkları A'dan başlayarak sırayla yazın",
  optionLength: `Şık ${LIMITS.optionMax} karakterden uzun olamaz`,
  correct: "Doğru cevap eksik ya da geçersiz",
};

/**
 * Excel içeriğini (ArrayBuffer/Uint8Array) doğrular.
 * Dönüş: { packages: [{ title, description, tags, questions, rows }], errors: string[] }.
 * errors doluysa packages boştur.
 */
export function parseLibraryWorkbook(data) {
  let workbook;
  try {
    workbook = XLSX.read(data, { type: "array" });
  } catch {
    return { packages: [], errors: ["Dosya okunamadı. Lütfen örnek Excel dosyasını kullanın."] };
  }
  const sheetName = workbook.SheetNames[0];
  const sheet = sheetName ? workbook.Sheets[sheetName] : null;
  if (!sheet) return { packages: [], errors: ["Dosyada sayfa bulunamadı."] };

  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
  const header = (matrix[0] || []).map((h) => String(h ?? "").trim());
  const headerOk =
    LIBRARY_HEADERS.every((h, i) => header[i] === h) &&
    header.slice(LIBRARY_HEADERS.length).every((h) => h === "");
  if (!headerOk) {
    return {
      packages: [],
      errors: ["İlk satırdaki başlıklar örnek dosyadakiyle birebir aynı olmalı. Lütfen örnek Excel dosyasını kullanın."],
    };
  }

  const errors = [];
  const byTitle = new Map();
  matrix.slice(1).forEach((cells, index) => {
    const line = index + 2;
    const cell = (i) => String(cells[i] ?? "").trim();
    if (cells.every((c) => String(c ?? "").trim() === "")) return; // boş satır atlanır
    const err = (text) => errors.push(`${line}. satır: ${text}`);

    if (cells.slice(LIBRARY_HEADERS.length).some((c) => String(c ?? "").trim() !== "")) {
      err("Başlıklı sütunların dışına yazılmış");
    }
    const title = cell(0);
    if (!title) {
      err("Paket adı boş olamaz");
      return;
    }
    if (title.length > LIMITS.titleMax) err(`Paket adı ${LIMITS.titleMax} karakterden uzun olamaz`);

    const key = title.toLocaleLowerCase("tr");
    if (!byTitle.has(key)) {
      byTitle.set(key, { title, description: "", tagsText: "", questions: [], rows: [] });
    }
    const pkg = byTitle.get(key);
    pkg.rows.push(line);

    const description = cell(1);
    if (description) {
      if (pkg.description && pkg.description !== description) err("Paketin açıklaması başka bir satırda farklı yazılmış");
      else pkg.description = description;
    }
    const tagsText = cell(2);
    if (tagsText) {
      if (pkg.tagsText && pkg.tagsText !== tagsText) err("Paketin etiketleri başka bir satırda farklı yazılmış");
      else pkg.tagsText = tagsText;
    }

    const type = parseQuestionType(cell(3));
    if (!type) {
      err(`Soru tipi şunlardan biri olmalı: ${Object.values(TYPE_NAMES).join(", ")}`);
      return;
    }
    const optionCells = OPTION_LETTERS.map((_, i) => cell(5 + i));
    const question = { type, text: cell(4) };
    if (type === "coktan") {
      // Son dolu şıka kadar alınır; aradaki boş şık hatadır.
      const last = optionCells.reduce((acc, o, i) => (o ? i : acc), -1);
      question.options = optionCells.slice(0, last + 1);
    } else if (optionCells.some(Boolean)) {
      err(`${TYPE_NAMES[type]} sorusunda şık yazılmaz`);
    }
    const correct = parseCorrect(type, cell(11), question.options?.length || 0);
    if (correct === null) {
      err(
        type === "acik"
          ? "Açık uçlu soruda doğru cevap yazılmaz"
          : type === "coktan"
            ? "Doğru cevap şıklardan birinin harfi (A–F) olmalı"
            : `Doğru cevap ${type === "evethayir" ? "Evet ya da Hayır" : "Doğru ya da Yanlış"} olmalı`
      );
    } else {
      Object.assign(question, correct);
    }
    const issue = questionIssue(question);
    if (issue && !(issue === "correct" && correct === null)) err(QUESTION_ISSUE_TEXT[issue] || "Soru geçersiz");
    pkg.questions.push(question);
  });

  const packages = [...byTitle.values()].map(({ tagsText, ...pkg }) => ({ ...pkg, tags: parseTags(tagsText) }));
  for (const pkg of packages) {
    const where = `"${pkg.title}" paketi`;
    if (pkg.description.length > LIMITS.descriptionMax) {
      errors.push(`${where}: Açıklama ${LIMITS.descriptionMax} karakterden uzun olamaz`);
    }
    if (pkg.tags.length > TAGS_MAX) errors.push(`${where}: En fazla ${TAGS_MAX} etiket yazılabilir`);
    if (pkg.tags.some((tag) => tag.length > TAG_MAX)) errors.push(`${where}: Etiket ${TAG_MAX} karakterden uzun olamaz`);
    if (pkg.questions.length > LIMITS.questionsMax) {
      errors.push(`${where}: Bir pakette en fazla ${LIMITS.questionsMax} soru olabilir`);
    }
  }
  if (packages.length === 0 && errors.length === 0) errors.push("Dosyada soru satırı yok.");
  if (packages.length > LIBRARY_MAX_PACKAGES) {
    errors.push(`Tek seferde en fazla ${LIBRARY_MAX_PACKAGES} paket yüklenebilir.`);
  }
  return { packages: errors.length ? [] : packages, errors };
}

/** Örnek dosya: iki paket, dört soru tipinin hepsi, ve doldurma rehberi. */
export function buildLibraryTemplate() {
  const blank = ["", "", "", "", "", ""];
  const sheet = XLSX.utils.aoa_to_sheet([
    LIBRARY_HEADERS,
    [
      "Sözler 10. Söz · Haşir",
      "Haşir risalesinin ilk makamı üzerine değerlendirme",
      "Sözler, Haşir",
      TYPE_NAMES.coktan,
      "Haşir Risalesi hangi eserin içindedir?",
      "Sözler",
      "Mektubat",
      "Lem'alar",
      "Şuâlar",
      "",
      "",
      "A",
    ],
    ["Sözler 10. Söz · Haşir", "", "", TYPE_NAMES.dogruyanlis, "Risalede on iki hakikat anlatılır.", ...blank, "Doğru"],
    ["Sözler 10. Söz · Haşir", "", "", TYPE_NAMES.acik, "Bu dersten sizde en çok kalan nokta nedir?", ...blank, ""],
    [
      "Lem'alar 1. Lem'a",
      "Hz. Yunus'un (as) münacatı",
      "Lem'alar",
      TYPE_NAMES.evethayir,
      "Münacatı bugün okudunuz mu?",
      ...blank,
      "Evet",
    ],
    [
      "Lem'alar 1. Lem'a",
      "",
      "",
      TYPE_NAMES.coktan,
      "Lem'ada kaç karanlıktan bahsedilir?",
      "İki",
      "Üç",
      "Dört",
      "",
      "",
      "",
      "B",
    ],
  ]);
  sheet["!cols"] = [
    { wch: 28 },
    { wch: 36 },
    { wch: 18 },
    { wch: 16 },
    { wch: 44 },
    ...OPTION_LETTERS.map(() => ({ wch: 14 })),
    { wch: 13 },
  ];

  const guide = XLSX.utils.aoa_to_sheet([
    ["Nasıl doldurulur?"],
    ["Soruları ilk sayfaya (Sorular) yazın; ilk satırdaki başlıkları değiştirmeyin. Her satır bir sorudur."],
    [`Paket: zorunlu, en fazla ${LIMITS.titleMax} karakter. Aynı paket adını taşıyan satırlar tek pakette toplanır; bir dosyada en fazla ${LIBRARY_MAX_PACKAGES} paket olabilir.`],
    [`Açıklama: isteğe bağlı, en fazla ${LIMITS.descriptionMax} karakter. Paketin herhangi bir satırına bir kez yazın.`],
    [`Etiketler: isteğe bağlı, virgülle ayırın; en fazla ${TAGS_MAX} etiket, her biri en fazla ${TAG_MAX} karakter.`],
    [`Soru Tipi: ${Object.values(TYPE_NAMES).join(" · ")}`],
    [`Soru: zorunlu, en fazla ${LIMITS.questionTextMax} karakter. Bir pakette en fazla ${LIMITS.questionsMax} soru.`],
    [`Şık A–F: yalnız çoktan seçmelide; ${LIMITS.optionsMin}–${LIMITS.optionsMax} şık, A'dan başlayarak sırayla, her biri en fazla ${LIMITS.optionMax} karakter.`],
    ["Doğru Cevap: çoktan seçmelide şık harfi (A–F); Evet / Hayır sorusunda Evet ya da Hayır; Doğru / Yanlış sorusunda Doğru ya da Yanlış; açık uçluda boş."],
    ["Paketler yüklenince size özel olarak oluşur; dilerseniz yüklerken onaya gönderebilirsiniz."],
    ["Tek bir satır bile hatalıysa dosyanın tamamı reddedilir; hatalar satır numarasıyla gösterilir."],
  ]);
  guide["!cols"] = [{ wch: 120 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Sorular");
  XLSX.utils.book_append_sheet(workbook, guide, "Nasıl Doldurulur");
  return workbook;
}

export function downloadLibraryTemplate() {
  XLSX.writeFile(buildLibraryTemplate(), LIBRARY_TEMPLATE_NAME);
}
